import React, { RefObject, useCallback, useEffect, useState } from 'react';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import useDebounce from '../../../../components/molecules/Form/components/ComboBox/AbstractComboBox/hooks/useDebounce';
import bem from '../../../../lib/bemUtils/bem';
import { Modifier } from '../../../../lib/bemUtils/Modifier';

export enum InputCounterModifier {
  VISIBLE = 'visible',
}

type InputCounterProps = {
  maxLength: number;
  // uncontrolled mode (Form 2): the counter listens to keyup events on the referenced element
  elementRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  // controlled mode (Form 1): the count is passed in as a prop
  currentCount?: number;
  modifier?: Modifier<InputCounterModifier>;
};

export function InputCounter({ maxLength, elementRef, currentCount, modifier }: InputCounterProps) {
  const { t } = useSonTranslation('InputCounter');
  const isUncontrolled = elementRef !== undefined;
  const [counterState, setCounterState] = useState<number>(currentCount ?? 0);
  const debouncedCounterState = useDebounce<number>(counterState, 300);
  const [message, setMessage] = useState<string>('');

  const displayedCount = isUncontrolled ? counterState : (currentCount ?? 0);

  /**
   * Calculates the remaining chars for screen reader output. Generates the message sent to screen readers.
   * This uses the debounce value so the message will be calculated when user stops typing (uncontrolled mode only)
   */
  const getScreenReaderOutput = useCallback((): string => {
    const count = isUncontrolled ? debouncedCounterState.value : (currentCount ?? 0);
    const remainingChars = maxLength - count;
    return remainingChars > 0
      ? t('screenReaderMessageWarning', { remainingChars })
      : t('screenReaderMessageError', { currentCount: count });
  }, [debouncedCounterState, isUncontrolled, currentCount, maxLength, t]);

  useEffect(() => {
    setMessage(getScreenReaderOutput());
  }, [getScreenReaderOutput]);

  useEffect(() => {
    const inputElement = elementRef?.current;
    // initialize input length with this here
    setCounterState(inputElement?.value?.length || 0);
    const setCountFromRef = (event: Event) => {
      const target = event.target as HTMLInputElement;
      setCounterState(target.value?.length || 0);
    };

    inputElement?.addEventListener('keyup', setCountFromRef);
    return () => {
      inputElement?.removeEventListener('keyup', setCountFromRef);
    };
  }, [elementRef]);

  /**
   * 10% of maxLength characters before maxLength we change the counter look to orange (warning).
   * When we reach maxlength, it's set to red (error).
   * We additionally add an invisible border for people using WIN HCM
   */
  const changeCounterLook = () => {
    const percentage = Math.round((10 / 100) * maxLength);
    const basicClasses = 'input-counter__count font-regular-11-11';

    if (displayedCount >= maxLength - percentage && displayedCount <= maxLength) {
      return basicClasses + ' input-counter__count--warning';
    }
    if (displayedCount > maxLength) {
      return basicClasses + ' input-counter__count--error';
    }
    return basicClasses;
  };

  /**
   * Counter consists of two elements: the counter and a corresponding live region.
   * Live region is display:none until corresponding input field is focused (see CSS).
   * Counter is aria-hidden="true" to prevent reading via screen reader focus.
   */
  return (
    <span className={bem('input-counter', modifier)}>
      <span className={changeCounterLook()} aria-hidden="true">
        {`${displayedCount} / ${maxLength}`}
      </span>
      <span aria-live="polite" aria-atomic="true" className="input-counter__live-region">
        {message}
      </span>
    </span>
  );
}
